import {objectFromEntries, pick, selectDefined} from '@benchling/js-utils';

import {AnalysisGraphQL} from 'analysis-tool/AnalysisTypes';
import {getViewForViewReference} from 'analysis-tool/data-view/AnalysisViewUtils';
import {assert} from 'Asserts';
import {ColumnTypeValidationFlag, ColumnTypeValidationOptions} from 'data-frames/util/validateColumnType';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  FIELD_ORDER_UI_SCHEMA_KEY,
  FieldSettingsV2,
  FieldUISchemaWithStandardComponentV2,
  FieldUISchemaActionButton,
  SECTION_NAME_UI_SCHEMA_KEY,
  SHOW_DIVIDER_LINE_KEY,
  CONDITIONAL_UI_SCHEMA_KEY,
  INFO_TEXT_UI_SCHEMA_KEY,
  IS_COLLAPSIBLE_SCHEMA_KEY,
  ShapeUISchemaV2,
  StepConfigEditorOverride,
  NUM_COLUMNS_KEY,
  FieldUISchemaMarkdown,
} from 'pipelines/generalized-step-config-editor/StepConfigEditorTypes';
import {PipelineStep} from 'pipelines/PipelineTypes';
import {PipelineConfigJsonSpec} from 'pipelines/StepShapeUtils';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {omit} from 'util/omitKey';
import {assertNever} from 'util/Types';

import {
  AnalysisConfigForm,
  AnalysisConfigFormField,
  AnalysisGrid,
  AnalysisGridBlock,
  AnalysisNestedGrid,
  AnalysisUISchemaNestedField,
  AnalysisUISchemaNestedShape,
  AnalysisUISchema,
  AnalysisUISchemaActionButton,
  AnalysisSection,
  AnalysisUISchemaMarkdown,
} from './analysis-adapter/AnalysisAdapterTypes';
import {resolveConfigReferenceAndDataFrameInfo} from './analysis-adapter/AnalysisAdapterUtils';
import {
  DataLayout,
  ConfigFormBlock,
  ConfigFormField,
  ConfigFormAction,
  GridSection,
  GridBlock,
  DataLayoutSection,
} from './DataLayoutTypes';

function convertColumnTypeValidationOptions(
  columnTypeValidationOptions: Extract<
    AnalysisConfigFormField['configSpec'],
    {kind: 'columnReference'}
  >['validationOptions']
): ColumnTypeValidationOptions | null {
  if (columnTypeValidationOptions == null) {
    return null;
  }

  return selectDefined({
    ...columnTypeValidationOptions,
    allowedLeafTypes:
      columnTypeValidationOptions.allowedLeafTypes == null
        ? undefined
        : columnTypeValidationOptions.allowedLeafTypes.map((leafType) => LeafType[leafType]),
    listType:
      columnTypeValidationOptions.listType == null
        ? undefined
        : ColumnTypeValidationFlag[columnTypeValidationOptions.listType],
    nullableType:
      columnTypeValidationOptions.nullableType == null
        ? undefined
        : ColumnTypeValidationFlag[columnTypeValidationOptions.nullableType],
  });
}

function convertAnalysisConfigSpec(
  configSpec: AnalysisConfigFormField['configSpec'],
  configFieldKey: string
): PipelineConfigJsonSpec {
  switch (configSpec.kind) {
    case 'columnReference':
      return {
        ...configSpec,
        inputDataName: configFieldKey,
        validationOptions: convertColumnTypeValidationOptions(configSpec.validationOptions),
      };
    case 'columnValue':
    case 'tableReference':
      return {
        ...configSpec,
        inputDataName: configFieldKey,
        inputDataIndex: 0,
      };
    case 'integer':
      return selectDefined({
        ...configSpec,
        minValue: configSpec.minValue ?? undefined,
        maxValue: configSpec.maxValue ?? undefined,
      });
    case 'string':
      return selectDefined({
        ...configSpec,
        value: configSpec.value ?? undefined,
      });
    case 'boolean':
    case 'date':
    case 'enum':
    case 'number':
      return configSpec;
    default:
      throw new Error(`Found unexpected config spec kind ${assertNever(configSpec).kind} in layout`);
  }
}

function convertConfigFormField(
  {configReferences, configSpec, ...configFormField}: AnalysisConfigFormField,
  layoutStep: PipelineStep<PipelineStepType.LAYOUT, 0>,
  analysis: AnalysisGraphQL
): ConfigFormField {
  const {config, configDataFrameInfo} = resolveConfigReferenceAndDataFrameInfo(
    configReferences[0],
    configSpec,
    layoutStep,
    analysis
  );

  return {
    ...configFormField,
    config,
    configDataFrameInfo,
    configShape: convertAnalysisConfigSpec(configSpec, configFormField.key),
  };
}

function convertFieldSettings(
  fieldSettings: AnalysisUISchemaNestedField['fieldSettings']
): FieldSettingsV2 | null {
  if (fieldSettings == null) {
    return null;
  }

  const fieldType = fieldSettings.type;
  const baseFieldSettings = omit(fieldSettings, 'type');

  switch (fieldType) {
    case 'CHECKBOX_SETTINGS':
      return {
        ...baseFieldSettings,
        checkboxLabel: fieldSettings.checkboxLabel,
      };
    case 'COLUMN_REFERENCE_SETTINGS':
      return selectDefined({
        ...baseFieldSettings,
        noColumnSelectedText: fieldSettings.noColumnSelectedText ?? undefined,
        allowSelectNull: fieldSettings.allowSelectNull ?? undefined,
      });
    case 'COLUMN_VALUE_SETTINGS':
      return selectDefined({
        ...baseFieldSettings,
        noValueSelectedText: fieldSettings.noValueSelectedText ?? undefined,
      });
    case 'ENUM_SETTINGS':
      return selectDefined({
        ...baseFieldSettings,
        displayNames:
          fieldSettings.displayNames == null
            ? undefined
            : objectFromEntries(
                fieldSettings.displayNames.map(({value, displayName}) => [value, displayName])
              ),
      });
    case 'ICON_SELECTOR_SETTINGS':
      return selectDefined({
        ...baseFieldSettings,
        displayNames:
          fieldSettings.displayNames == null
            ? undefined
            : objectFromEntries(
                fieldSettings.displayNames.map(({value, displayName}) => [value, displayName])
              ),
        iconNames:
          fieldSettings.iconNames == null
            ? undefined
            : objectFromEntries(
                fieldSettings.iconNames.map(({value, iconName}) => [value, iconName as BenchlingIconName])
              ),
      });
    case 'STRING_FIELD_SETTINGS':
      return {
        ...baseFieldSettings,
        useTextArea: fieldSettings.useTextArea ?? false,
      };
    case 'TABLE_REFERENCE_SETTINGS':
      return selectDefined({
        ...baseFieldSettings,
        disabledColumn: fieldSettings.disabledColumn ?? undefined,
        hiddenColumn: fieldSettings.hiddenColumn ?? undefined,
      });
    case 'TAGGED_UNION_SETTINGS':
      return selectDefined({
        ...baseFieldSettings,
        tagKeyDisplayNames:
          fieldSettings.tagKeyDisplayNames == null
            ? undefined
            : objectFromEntries(
                fieldSettings.tagKeyDisplayNames.map(({value, displayName}) => [value, displayName])
              ),
      });
    case 'RANGE_SETTINGS':
      return selectDefined({
        ...baseFieldSettings,
        startKey: fieldSettings.startKey ?? undefined,
        endKey: fieldSettings.endKey ?? undefined,
      });
    default:
      throw new Error(`Found unexpected uiSchema field type ${assertNever(fieldType)} in layout`);
  }
}

function convertUISchemaField(
  key: string,
  uiSchema: AnalysisUISchemaNestedField
): FieldUISchemaWithStandardComponentV2 {
  return selectDefined({
    ...omit(uiSchema, 'type'),
    configShapePath: [key],
    displayName: uiSchema.displayName ?? undefined,
    infoText: uiSchema.infoText ?? undefined,
    editorOverride:
      uiSchema.editorOverride == null ? undefined : StepConfigEditorOverride[uiSchema.editorOverride],
    fieldSettings: convertFieldSettings(uiSchema.fieldSettings) ?? undefined,
  });
}

function convertUISchemaActionButton(
  _key: string,
  uiSchema: AnalysisUISchemaActionButton
): FieldUISchemaActionButton {
  return {
    ...selectDefined({
      key: ConfigFormAction[uiSchema.action],
      buttonText: uiSchema.buttonText,
      buttonIcon: uiSchema.buttonIcon ?? undefined,
      buttonVariant: uiSchema.buttonVariant ?? undefined,
      helpText: uiSchema.helpText ?? undefined,
    }),
  };
}

function convertUISchemaMarkdown(_key: string, uiSchema: AnalysisUISchemaMarkdown): FieldUISchemaMarkdown {
  return pick(uiSchema, 'markdown');
}

function convertUISchemaNestedField(
  key: string,
  uiSchema:
    | AnalysisUISchemaNestedField
    | AnalysisUISchemaNestedShape
    | AnalysisUISchemaActionButton
    | AnalysisUISchemaMarkdown
):
  | ShapeUISchemaV2
  | FieldUISchemaWithStandardComponentV2
  | FieldUISchemaActionButton
  | FieldUISchemaMarkdown {
  if (uiSchema.type === 'FIELD') {
    return convertUISchemaField(key, uiSchema);
  } else if (uiSchema.type === 'ACTION_BUTTON') {
    return convertUISchemaActionButton(key, uiSchema);
  } else if (uiSchema.type === 'MARKDOWN') {
    return convertUISchemaMarkdown(key, uiSchema);
  } else if (uiSchema.type === 'SHAPE') {
    return convertUISchema(uiSchema);
  }

  throw new Error(`Found unexpected uiSchema type ${assertNever(uiSchema).type} in layout`);
}

function convertUISchema(uiSchema: AnalysisUISchema | AnalysisUISchemaNestedShape): ShapeUISchemaV2 {
  return {
    [FIELD_ORDER_UI_SCHEMA_KEY]: uiSchema.fields.map(({key}) => key),
    [SECTION_NAME_UI_SCHEMA_KEY]: uiSchema.sectionName ?? undefined,
    [SHOW_DIVIDER_LINE_KEY]: uiSchema.showDividerLine ?? undefined,
    [CONDITIONAL_UI_SCHEMA_KEY]: uiSchema.conditional ?? undefined,
    [INFO_TEXT_UI_SCHEMA_KEY]: uiSchema.infoText ?? undefined,
    [IS_COLLAPSIBLE_SCHEMA_KEY]: uiSchema.isCollapsible ?? undefined,
    [NUM_COLUMNS_KEY]: uiSchema.numColumns ?? undefined,
    ...objectFromEntries(
      uiSchema.fields.map(({key, uiSchema}) => [key, convertUISchemaNestedField(key, uiSchema)])
    ),
  };
}

function convertConfigForm(
  {configFields, uiSchema, ...configForm}: AnalysisConfigForm,
  layoutStep: PipelineStep<PipelineStepType.LAYOUT, 0>,
  analysis: AnalysisGraphQL
): ConfigFormBlock {
  return {
    ...configForm,
    configFields: configFields.map((configFormField) =>
      convertConfigFormField(configFormField, layoutStep, analysis)
    ),
    uiSchema: convertUISchema(uiSchema),
  };
}

function resolveGridBlocks(
  analysis: AnalysisGraphQL,
  layoutStep: PipelineStep<PipelineStepType.LAYOUT, 0>,
  block: Exclude<AnalysisGridBlock, AnalysisNestedGrid>
): GridBlock {
  if (block.type === 'DATA') {
    const matchingIncomingEdge = layoutStep.incomingEdges.find(
      (incomingEdge) =>
        incomingEdge.toStepData.name === block.inputReference.inputDataName &&
        incomingEdge.toStepDataIndex === block.inputReference.inputDataIndex
    )!;
    return {
      ...omit(block, 'inputReference'),
      view: getViewForViewReference(analysis, {
        stepId: matchingIncomingEdge.fromStep.id,
        outputReference: {
          stepDataName: matchingIncomingEdge.fromStepData.name,
          rawDataIndex: matchingIncomingEdge.fromStepDataIndex,
        },
      })!,
    };
  } else if (block.type === 'CONFIG_FORM') {
    return convertConfigForm(block, layoutStep, analysis);
  }

  return block;
}

function hasNoNestedGrids(
  blocks: Array<AnalysisGridBlock>
): blocks is Array<Exclude<AnalysisGridBlock, AnalysisNestedGrid>> {
  return !blocks.some((block) => block.type === 'NESTED_GRID');
}

function convertGridRows(
  rows: AnalysisGrid['rows'],
  layoutStep: PipelineStep<PipelineStepType.LAYOUT, 0>,
  analysis: AnalysisGraphQL
): GridSection['rows'] {
  return rows.map((row) =>
    row.map((block: AnalysisGridBlock) => {
      if (block.type === 'NESTED_GRID') {
        assert(hasNoNestedGrids(block.rows.flat()), 'Grids are only supported one row deep');
        return {
          ...block,
          rows: block.rows.map((nestedRow) =>
            nestedRow.map((nestedBlock) => resolveGridBlocks(analysis, layoutStep, nestedBlock))
          ),
        };
      }
      return resolveGridBlocks(analysis, layoutStep, block);
    })
  );
}

function convertSection(
  section: AnalysisSection,
  layoutStep: PipelineStep<PipelineStepType.LAYOUT, 0>,
  analysis: AnalysisGraphQL
): DataLayoutSection {
  if (section.type === 'GRID') {
    return {
      ...section,
      gridMode: section.gridMode,
      rows: convertGridRows(section.rows, layoutStep, analysis),
    };
  } else if (section.type === 'TAB') {
    return {
      ...section,
      tabs: section.tabs.map((tab) => ({...tab, rows: convertGridRows(tab.rows, layoutStep, analysis)})),
    };
  } else {
    throw new Error(`Found unexpected section type ${assertNever(section).type} in layout`);
  }
}

export default function pipelineLayoutStepToDataLayout(
  layoutStep: PipelineStep<PipelineStepType.LAYOUT, 0>,
  analysis: AnalysisGraphQL
): DataLayout {
  const analysisDataLayout = layoutStep.config!;
  const mappedSections = analysisDataLayout.sections.map((section) =>
    convertSection(section, layoutStep, analysis)
  );
  return {...pick(analysisDataLayout, 'name'), sections: mappedSections};
}
